import React from "react";
import { Play } from "lucide-react";
import { playerInfo, photos } from "../mock/mock";
import { useLanguage } from "../contexts/LanguageContext";
import BrandDivider from "./BrandDivider";

const Hero = () => {
  const { t } = useLanguage();

  const scrollToVideo = () => {
    const el = document.getElementById("video");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative min-h-[100svh] w-full overflow-hidden bg-marseille">
      {/* Background photo — opacity 1, sharp on all breakpoints. Responsive WebP for faster LCP. */}
      <div className="absolute inset-0" aria-hidden="true">
        <picture>
          <source
            type="image/webp"
            srcSet="/images/hero-480.webp 480w, /images/hero-900.webp 900w, /images/hero-1200.webp 1200w, /images/hero-1600.webp 1600w"
            sizes="100vw"
          />
          <img
            src={photos.hero}
            alt=""
            className="w-full h-full object-cover object-center sm:object-[center_30%] md:object-[center_20%]"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            width="1600"
            height="900"
          />
        </picture>
        {/* Top gradient (header safety area) — covers only the top ~110px so the white logo stays legible over the photo */}
        <div
          className="absolute inset-x-0 top-0 h-28 md:h-32 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(16,58,94,0.75) 0%, rgba(31,109,176,0.35) 55%, rgba(31,109,176,0) 100%)"
          }}
        />
        {/* Mobile / tablet: gradient only behind the TEXT at the BOTTOM. Top of photo stays untouched. */}
        <div
          className="absolute inset-x-0 bottom-0 h-[82%] md:hidden pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, #103A5E 0%, rgba(31,109,176,0.92) 20%, rgba(31,109,176,0.7) 48%, rgba(31,109,176,0.35) 78%, rgba(31,109,176,0) 100%)"
          }}
        />
        {/* Desktop: horizontal gradient only on the LEFT zone (text area). Right half of photo stays at opacity 1. */}
        <div
          className="hidden md:block absolute inset-y-0 left-0 w-[62%] lg:w-[55%] pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, #1F6DB0 0%, rgba(31,109,176,0.78) 35%, rgba(31,109,176,0.3) 70%, rgba(31,109,176,0) 100%)"
          }}
        />
      </div>

      {/* Decorative 3+1 lines, very subtle, top-left corner */}
      <div className="absolute inset-x-0 top-24 md:top-28 opacity-25 deco-layer" aria-hidden="true">
        <BrandDivider color="#FFFFFF" opacity={0.4} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 md:pt-36 pb-14 min-h-[100svh] flex flex-col justify-end">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-[1px] bg-white" />
            <span
              data-testid="hero-eyebrow"
              className="text-[10.5px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.3em] whitespace-nowrap text-white font-medium bg-marseille-dark/90 px-2.5 py-1.5 md:bg-transparent md:p-0 hero-text-shadow"
            >
              {t("hero.eyebrow")}
            </span>
          </div>

          <h1 className="font-hero text-white hero-text-shadow">
            <span
              className="block text-[16vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[140px]"
              style={{ fontWeight: 300 }}
            >
              Emily
            </span>
            <span
              className="block text-[16vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[140px] -mt-2 md:-mt-4"
              style={{ fontWeight: 700 }}
            >
              Burns
            </span>
          </h1>

          <p className="mt-6 text-white text-base md:text-lg max-w-xl font-normal leading-relaxed hero-text-shadow">
            {t("hero.subheadline")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-watch-reel-btn"
              onClick={scrollToVideo}
              className="group inline-flex items-center gap-3 px-6 py-3.5 bg-white text-marseille text-[11.5px] uppercase tracking-[0.22em] font-semibold hover:bg-caliza transition-colors"
            >
              <Play size={14} className="fill-current" />
              {t("hero.watchReel")}
            </button>
            <a
              href="#profile"
              data-testid="hero-view-profile-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/85 bg-marseille-dark/90 backdrop-blur-sm text-white text-[11.5px] uppercase tracking-[0.22em] font-semibold hover:bg-marseille-dark transition-colors"
            >
              {t("hero.viewProfile")}
            </a>
          </div>
        </div>

        {/* Meta bar — wrapped in a dark card so values stay AA-contrast over the photo on tablet/desktop */}
        <div className="mt-10 md:mt-14 bg-marseille-dark/90 backdrop-blur-sm border-t-2 border-white/40 px-5 md:px-7 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 text-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/90 mb-2 font-medium">
              {t("hero.position")}
            </div>
            <div className="text-base md:text-lg uppercase font-semibold">{t("footer.role")}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/90 mb-2 font-medium">
              {t("hero.nationality")}
            </div>
            <div className="text-base md:text-lg uppercase font-semibold">
              {playerInfo.nationality}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/90 mb-2 font-medium">
              {t("hero.height")}
            </div>
            <div className="text-base md:text-lg uppercase font-semibold">{playerInfo.height}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/90 mb-2 font-medium">
              {t("hero.currentClub")}
            </div>
            <div className="text-base md:text-lg uppercase font-semibold leading-tight">
              {playerInfo.currentClub}
            </div>
          </div>
        </div>

        {/* Scroll indicator removed to avoid overlapping the meta row on tablet/desktop */}
      </div>
    </section>
  );
};

export default Hero;
